// Image processing: orientation-correct downscaling for captured photos, thumbnail
// generation, and offline-safe placeholder art for the seeded demo trip (spec §4.6 —
// "preserve photo orientation correctly", "generate a smaller thumbnail").

const ORIGINAL_MAX_DIM = 1600;
const THUMB_DIM = 480;

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

async function canvasToBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  type = 'image/jpeg',
  quality = 0.86
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type, quality });
  }
  return new Promise((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))),
      type,
      quality
    );
  });
}

function getCtx(canvas: OffscreenCanvas | HTMLCanvasElement) {
  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error('2D canvas context unavailable');
  return ctx;
}

/** Downscale + re-encode a captured photo, then produce a square cover-cropped thumbnail.
 *  Uses createImageBitmap's `imageOrientation: 'from-image'` so EXIF-rotated phone
 *  photos come out upright without a manual EXIF parser. */
export async function createOriginalAndThumbnail(
  file: Blob
): Promise<{ original: Blob; thumbnail: Blob }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, ORIGINAL_MAX_DIM / Math.max(bitmap.width, bitmap.height));
    const ow = Math.max(1, Math.round(bitmap.width * scale));
    const oh = Math.max(1, Math.round(bitmap.height * scale));
    const oc = makeCanvas(ow, oh);
    getCtx(oc).drawImage(bitmap, 0, 0, ow, oh);
    const original = await canvasToBlob(oc, 'image/jpeg', 0.86);

    // Cover-crop to a square thumbnail.
    const side = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;
    const tc = makeCanvas(THUMB_DIM, THUMB_DIM);
    getCtx(tc).drawImage(bitmap, sx, sy, side, side, 0, 0, THUMB_DIM, THUMB_DIM);
    const thumbnail = await canvasToBlob(tc, 'image/jpeg', 0.82);

    return { original, thumbnail };
  } finally {
    bitmap.close();
  }
}

/** Locally-drawn placeholder art for the seeded demo trip — used instead of a remote
 *  placeholder-image service so the sample data is genuinely available offline from
 *  first launch (no network dependency for demo content). */
export async function generatePlaceholderPhoto(
  label: string,
  seed: number
): Promise<{ original: Blob; thumbnail: Blob }> {
  const hue = (seed * 47) % 360;
  const draw = (canvas: OffscreenCanvas | HTMLCanvasElement, w: number, h: number) => {
    const ctx = getCtx(canvas);
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, `hsl(${hue} 45% 16%)`);
    grad.addColorStop(1, `hsl(${(hue + 40) % 360} 40% 8%)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = 'rgba(57,217,122,0.16)';
    ctx.beginPath();
    ctx.arc(w * 0.78, h * 0.22, Math.min(w, h) * 0.32, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = `600 ${Math.round(Math.min(w, h) * 0.09)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const words = label.split(' ');
    const line1 = words.slice(0, Math.ceil(words.length / 2)).join(' ');
    const line2 = words.slice(Math.ceil(words.length / 2)).join(' ');
    ctx.fillText(line1, w / 2, h / 2 - Math.min(w, h) * 0.06);
    if (line2) ctx.fillText(line2, w / 2, h / 2 + Math.min(w, h) * 0.06);
  };
  const oc = makeCanvas(900, 675);
  draw(oc, 900, 675);
  const original = await canvasToBlob(oc, 'image/jpeg', 0.85);
  const tc = makeCanvas(THUMB_DIM, THUMB_DIM);
  draw(tc, THUMB_DIM, THUMB_DIM);
  const thumbnail = await canvasToBlob(tc, 'image/jpeg', 0.82);
  return { original, thumbnail };
}

export function blobToObjectURL(blob: Blob): string {
  return URL.createObjectURL(blob);
}
